import React, { useEffect, useReducer } from 'react'
import { useParams } from 'react-router-dom'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import isEmpty from 'lodash/isEmpty'

import ParticipantProfile from 'components/ParticipantProfile'
import { fetchParticipant } from 'api/Participant/Action'
import { ParticipantSummary } from 'types/ParticipantProfile'
import NotFound from 'components/Shared/ErrorPages/404'
import { DUPLICATE_TAG_ERR_STATUS } from 'lib/GlobalConstants'
import { isPresent } from 'lib/Helper'
import {
  setLoading,
  setNotFound,
  setParticipantTags,
  setParticipantSummary,
  setParticipantTagsError,
  setProjects,
  setNotesProjectId,
  setActivitiesProjectId,
  setNewNotesCount,
  setSignupFormAnswers,
  setSignupFormErrors,
} from 'containers/ParticipantProfile/Redux/Actions'
import participantProfileReducer, {
  initialState as state,
} from 'containers/ParticipantProfile/Redux/Reducer'
import {
  fetchParticipantTags,
  addTag,
} from 'api/Participant/ParticipantProfileTags/Action'
import { FetchTagsResponse } from 'types/Participants'
import { makeDefaultOpenFilterFalse } from 'lib/participants/Helper'
import { fetchProjects } from 'api/Project/Action'
import { Project } from 'types/Project'
import { fetchParticipantTags as fetchParticipantTagsAction } from 'redux/thunks/ParticipantTags'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { setFilters } from 'redux/slices/FilterBuilder'
import {
  resetAnswers,
  resetErrors,
  setAllAnswers,
  setInitialAnswers,
  resetInitialAnswers,
} from 'redux/slices/SignupFormAnswers'
import { fetchParticipantProfileSignupFormQuestions } from 'redux/thunks/SignupFormQuestions'

export const ParticipantProfileContext = React.createContext<any>({
  state: state,
})

// https://stackoverflow.com/questions/68399257/typescript-neither-either-but-not-both-properties
interface answerwithValue {
  value: IndexType | string
  id?: never
}
interface answerwithId {
  id: IndexType | string
  value?: never
}

const ParticipantProfileContainer = () => {
  const [componentState, dispatch] = useReducer(
    participantProfileReducer,
    state,
  )
  const { id } = useParams() as { id: string }
  const requestProjects = async () => {
    const response = await fetchProjects()
    const projects = response.map((obj: Project) => pick(obj, ['id', 'name']))
    dispatch(setProjects(projects))
  }

  const isAnswerValid = (answer: answerwithId | answerwithValue): boolean => {
    if (
      typeof answer === 'object' &&
      typeof answer.value === 'object' &&
      answer !== null &&
      answer.value
    ) {
      // Check for the text property in the object
      return answer.value.text
    }
    return answer?.value?.trim() !== '' && answer?.id !== '' // Return false if neither condition is met
  }

  const globalDispatch = useAppDispatch()

  const { selectedFilters } = useAppSelector((state) => state.filterBuilder)

  const onAddingTag = async (name: string) => {
    if (name) {
      const response = await addTag(id, name)

      if (response.status == DUPLICATE_TAG_ERR_STATUS) {
        dispatch(setParticipantTagsError(true))

        return
      }

      dispatch(setParticipantTagsError(false))
      requestTags()
      return response
    }
  }

  const removeBlankResponse = (answers: IndexType[]) => {
    return pickBy(
      answers,
      (answer: answerwithValue | answerwithId) =>
        !isEmpty(answer) && isAnswerValid(answer),
    )
  }

  const getParticipant = async () => {
    const participantResponse: any = (await fetchParticipant(
      id,
    )) as ParticipantSummary
    if (
      isPresent(participantResponse.status) &&
      participantResponse.status === 404
    ) {
      dispatch(setNotFound(true))
    } else {
      dispatch(setParticipantSummary(participantResponse))

      const modifiedSignupFormResponse = removeBlankResponse(
        participantResponse.signupFormResponse?.answers,
      )

      globalDispatch(setAllAnswers(modifiedSignupFormResponse))
      globalDispatch(setInitialAnswers(modifiedSignupFormResponse))
    }
  }

  const updateNewNoteCount = async () => {
    dispatch(setNotesProjectId(''))
    dispatch(
      setNewNotesCount(componentState.participantNotes.newNotesCount + 1),
    )
  }

  const requestTags = async () => {
    const response: FetchTagsResponse = await fetchParticipantTags(id)
    if (response.tags) dispatch(setParticipantTags(response.tags))
  }

  const setProjectIdForNotesFilter = (projectId: string) => {
    dispatch(setNotesProjectId(projectId))
  }

  const setProjectIdForActivitiesFilter = (projectId: string) => {
    dispatch(setActivitiesProjectId(projectId))
  }

  const updateSignupFormAnswer = (key: string, value: IndexType) => {
    dispatch(setSignupFormAnswers(key, value))
  }

  const updateSignupFormAnswerErrors = (value: []) => {
    dispatch(setSignupFormErrors(value))
  }

  useEffect(() => {
    dispatch(setLoading(true))
    globalDispatch(resetInitialAnswers())
    globalDispatch(resetAnswers())
    globalDispatch(resetErrors())
    getParticipant()
    requestTags()
    updateNewNoteCount()
    requestProjects()

    globalDispatch(fetchParticipantTagsAction())
    globalDispatch(setFilters(makeDefaultOpenFilterFalse(selectedFilters)))
    globalDispatch(fetchParticipantProfileSignupFormQuestions())
  }, [])

  return (
    <>
      {componentState.notFound ? (
        <NotFound />
      ) : (
        <ParticipantProfileContext.Provider
          value={{
            state: componentState,
            functions: {
              onAddingTag,
              updateNewNoteCount,
              getParticipant,
              setProjectIdForNotesFilter,
              setProjectIdForActivitiesFilter,
              dispatch,
              updateSignupFormAnswer,
              updateSignupFormAnswerErrors,
            },
          }}
        >
          <ParticipantProfile />
        </ParticipantProfileContext.Provider>
      )}
    </>
  )
}

export default ParticipantProfileContainer
